import { useEffect } from "react";
import { useHistory, useLocation } from "react-router";
import { getCurrentMultisiteBase } from "util/MagnoliaAppHelpers";
import {
  detectBrowserLanguage,
  getMultisiteDefaultLanguage,
  getMultisiteLanguages,
  hasLanguageInUrl,
  isSingleTreeMultisite,
} from "util/multisiteLanguages";

/**
 * Component that redirects users to the appropriate language path
 * when accessing a single-tree multisite without a language code in the URL.
 * 
 * For example:
 * - /albis-distribution/about → /albis-distribution/en/about (dev)
 * - /about → /en/about (production)
 * 
 * Note: Does NOT redirect in Magnolia preview mode - getCurrentLanguage() handles
 * language detection in that case.
 */
const LanguageDetectionRedirect = () => {
  const location = useLocation();
  const history = useHistory();
  const multisiteBase = getCurrentMultisiteBase();

  useEffect(() => {
    // Only handle single-tree multisites
    if (!isSingleTreeMultisite(multisiteBase)) {
      return;
    }

    // Skip redirect in Magnolia editor/preview mode
    // getCurrentLanguage() will return the default language in this case
    if (location.search.includes("mgnlPreview=")) {
      return;
    }

    const availableLanguages = getMultisiteLanguages(multisiteBase);
    const pathname = location.pathname;

    // Check if URL already has a language code
    if (hasLanguageInUrl(pathname, multisiteBase, availableLanguages)) {
      return;
    }

    // Skip redirect for root path (handled by LanguageRedirect component)
    // Also skip multisite root path (e.g., /albis-distribution or /albis-distribution/)
    if (
      pathname === "/" ||
      pathname === `/${multisiteBase}` ||
      pathname === `/${multisiteBase}/`
    ) {
      return;
    }

    // Detect best matching language
    const defaultLanguage = getMultisiteDefaultLanguage(multisiteBase);
    const detectedLanguage = detectBrowserLanguage(availableLanguages, defaultLanguage);

    // Build redirect URL with language
    let redirectUrl: string;

    if (process.env.NODE_ENV === "production") {
      // Production: /about → /en/about
      redirectUrl = `/${detectedLanguage}${pathname}`;
    } else {
      // Development: /albis-distribution/about → /albis-distribution/en/about
      // Remove trailing slash if present
      const cleanPath = pathname.endsWith("/") ? pathname.slice(0, -1) : pathname;
      const pathAfterMultisite = cleanPath.substring(`/${multisiteBase}`.length);
      redirectUrl = `/${multisiteBase}/${detectedLanguage}${pathAfterMultisite}`;
    }

    // Add query string and hash if present
    if (location.search) {
      redirectUrl += location.search;
    }
    if (location.hash) {
      redirectUrl += location.hash;
    }

    // Perform redirect
    console.log(`[Language Detection] Redirecting from ${pathname} to ${redirectUrl}`);
    history.replace(redirectUrl);
  }, [location, history, multisiteBase]);

  return null;
};

export default LanguageDetectionRedirect;

